import React from "react";
import { useParams } from "react-router-dom";
import { Center, Heading, HStack, Stack } from "@commonsku/design-system";
import { SkubotSpinner } from "@commonsku/styles";
import {
  WorkflowCondition,
  WorkflowConditionInput,
  WorkflowTask,
} from "../../../types";
import AutomationEarlyAccessAlert from "../automations-page/AutomationEarlyAccessAlert";
import AutomationEnableSwitch from "../automations-page/AutomationEnableSwitch";
import AutomationStepConnector from "./AutomationStepConnector";
import AutomationEditorHeader from "./AutomationEditorHeader";
import ConditionsStep from "./ConditionsStep";
import TriggerStep from "./TriggerStep";
import { useWorkflowAutomation } from "./useWorkflowAutomation";
import ActionsStep from "./ActionsStep";

/**
 * Top-level page for the automation editor screen (`/automations/:workflowId`).
 */
const AutomationEditorPage = () => {
  const { workflowId } = useParams();
  const {
    workflow,
    configurationOptions,
    loading,
    saving,
    error,
    isDirty,
    setWorkflow,
    save,
    cancel,
  } = useWorkflowAutomation(workflowId ?? "");

  if (loading || !workflow || !configurationOptions) {
    return (
      <Center paddingY="12">
        <SkubotSpinner />
      </Center>
    );
  }

  const handleTriggerChange = (trigger: WorkflowConditionInput) => {
    if (!workflow.trigger) {
      return;
    }

    setWorkflow({
      ...workflow,
      trigger: {
        ...workflow.trigger,
        ...trigger,
      },
    });
  };

  const handleConditionChange = (updatedCondition: WorkflowCondition) => {
    setWorkflow({
      ...workflow,
      conditions: workflow.conditions.map<WorkflowCondition>((condition) =>
        condition.workflow_condition_id === updatedCondition.workflow_condition_id
          ? updatedCondition
          : condition,
      ),
    });
  };

  const handleTaskChange = (updatedTask: WorkflowTask) => {
    setWorkflow({
      ...workflow,
      tasks: workflow.tasks.map<WorkflowTask>((task) =>
        task.workflow_task_id === updatedTask.workflow_task_id
          ? updatedTask
          : task,
      ),
    });
  };

  return (
    <Stack gap="6" paddingBottom="4" paddingRight="4" align="stretch">
      <AutomationEditorHeader
        workflowName={workflow.name}
        isDirty={isDirty}
        saving={saving}
        error={error}
        onSave={save}
        onCancel={cancel}
      />
      <AutomationEarlyAccessAlert />
      <HStack justify="space-between" align="center" width="full">
        <Heading size="lg" margin="0!">
          {workflow.name}
        </Heading>
        <AutomationEnableSwitch
          disabled={false}
          isChecked={workflow.active}
          label="Enable automation"
          onCheckedChange={(active) => setWorkflow({ ...workflow, active })}
        />
      </HStack>
      <Stack gap="0" align="stretch">
        <TriggerStep
          configurationOptions={configurationOptions}
          modelType={workflow.model_type}
          trigger={workflow.trigger}
          onChange={handleTriggerChange}
        />
        <AutomationStepConnector />
        <ConditionsStep
          conditions={workflow.conditions}
          configurationOptions={configurationOptions}
          modelType={workflow.model_type}
          onChange={handleConditionChange}
        />
        <AutomationStepConnector />
        <ActionsStep
          tasks={workflow.tasks}
          modelType={workflow.model_type}
          configurationOptions={configurationOptions}
          onChange={handleTaskChange}
        />
      </Stack>
    </Stack>
  );
};

export default AutomationEditorPage;
